import React, { useState, useEffect, useRef } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import useManager from './useManager'
import useManagerSettings from './hooks/useManagerSettings'
import Navbar from '../NavBar/NavBar'
import DocksManagerSchedules from './Components/DocksManagerSchedules'
import {LoadingOverlay} from '@achmadk/react-loading-overlay';
import PoweredByFooter from '../Footer/PoweredByFooter'
import ManagerLoading from './Components/ManagerLoading'
import Updating from '../Carrier/Components/Updating'
import Unauthorized from '../Carrier/Components/Unauthorized'
import DockSelector from './Components/DockSelector'
import UnScheduledShipments from './Components/UnScheduledShipments/UnScheduledShipments'
import { SelectedDateProvider } from '../../utils/Contexts/SelectedDateContext'
import UnapprovedAppts from './Components/UnapprovedAppts/UnapprovedAppts'
import ModeDropDown from './Components/ModeDropDown/ModeDropDown'
import AuthUI from './Components/AuthUI/AuthUI'
import SettingsModal from './Components/SettingsModal/SettingsModal'

const Manager = (props) => {

    useEffect(() => {
        console.log(
            '%c🚚 ShippersEdge Dock Scheduler — Manager View 🚢\n' +
            '%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
            '%c  Welcome, Manager! \n' +
            '  Manage schedules, approve appointments, and keep\n' +
            '  freight moving like a well-oiled machine. 📦✅',
            'color: #0d6efd; font-size: 16px; font-weight: bold;',
            'color: #6c757d;',
            'color: #198754; font-size: 12px;'
        )
    }, [])

    const { pageMode, docks, setDocks, usercode, status, setStatus, getManagerView, updatingOverlayStatus, setUpdatingOverlayStatus, debug, refreshData, unScheduledShipments, userRole, locationSettings, location, lastUpdateDate, urlDate } = useManager(props)

    const { settings, updateSettings } = useManagerSettings(location)

    const [selectedDock, setSelectedDock] = useState(null)

    const [managerMode, setManagerMode] = useState('calendar')

    const [managerModeBtnText, setManagerModeBtnText] = useState('Show Unscheduled Shipments')

    const [showSettingsModal, setShowSettingsModal] = useState(false)

    // https://ej2.syncfusion.com/react/documentation/schedule/resources/
    const [ownerData, setOwnerData] = useState(null)

    // Refs and handlers for synced top + bottom horizontal scrollbars
    const topScrollRef = useRef(null)
    const mainScrollRef = useRef(null)
    const syncingScroll = useRef(false)

    const schedulerContainerRef = useRef(null)
    const [schedulerHeight, setSchedulerHeight] = useState('calc(100vh - 160px)')

    // Sticky time overlay — driven entirely through DOM refs to avoid re-renders on every scroll tick
    const overlayRef = useRef(null)
    const overlayInnerRef = useRef(null)
    const contentWrapRef = useRef(null)
    const contentScrollHandlerRef = useRef(null)
    const TIME_COLUMN_WIDTH = 62

    function applyOptimisticUpdate(changedData) {
        const eventId = changedData.id
        const newStartTime = new Date(changedData.StartTime)
        const newEndTime = new Date(changedData.EndTime)

        if (!eventId || isNaN(newStartTime.getTime()) || isNaN(newEndTime.getTime())) return

        setDocks(prevDocks => prevDocks.map(dock => {
            const idx = dock.events.findIndex(e => e.id === eventId)
            if (idx === -1) return dock

            const updated = { ...dock.events[idx],
                StartTime: newStartTime,
                EndTime: newEndTime,
                timeIn: newStartTime.toISOString(),
                timeOut: newEndTime.toISOString(),
            }
            // Cross-dock: update ownerId so Syncfusion renders in the correct column.
            // The event stays in this dock's array — refreshData() does the structural move.
            if (changedData.ownerId != null) updated.ownerId = changedData.ownerId

            const updatedEvents = [...dock.events]
            updatedEvents[idx] = updated
            return { ...dock, events: updatedEvents }
        }))
    }

    const handleScheduleReady = () => {
        if (!mainScrollRef.current || !overlayRef.current || !overlayInnerRef.current) return

        const scheduleEl = mainScrollRef.current.querySelector('.e-schedule')
        if (!scheduleEl) return

        const timeCellsWrap = scheduleEl.querySelector('.e-time-cells-wrap')
        if (!timeCellsWrap) return  // month/agenda views have no time column

        // .e-schedule-scrollable is the element that actually scrolls vertically in Syncfusion v26
        const scrollContainer = timeCellsWrap.closest('.e-schedule-scrollable') || timeCellsWrap.parentElement
        if (!scrollContainer) return

        const scrollRect = scrollContainer.getBoundingClientRect()
        overlayRef.current.style.top = scrollRect.top + 'px'
        overlayRef.current.style.height = scrollRect.height + 'px'
        overlayRef.current.style.width = (timeCellsWrap.offsetWidth || TIME_COLUMN_WIDTH) + 'px'

        // Measure actual rendered row heights before copying — Syncfusion sets heights via CSS
        // chains that don't survive an innerHTML move, so we capture and re-apply them explicitly.
        const srcRows = Array.from(timeCellsWrap.querySelectorAll('tr'))
        const rowHeights = srcRows.map(r => r.offsetHeight)

        // Add e-time-cells-wrap class so Syncfusion's own CSS (e.g. cell fonts, borders) still applies
        overlayInnerRef.current.className = 'e-time-cells-wrap'
        overlayInnerRef.current.innerHTML = timeCellsWrap.innerHTML

        // Re-apply measured heights as inline styles so the copy matches the live element
        Array.from(overlayInnerRef.current.querySelectorAll('tr')).forEach((row, i) => {
            if (rowHeights[i]) row.style.height = rowHeights[i] + 'px'
        })

        // Re-attach vertical scroll sync via capture phase on the schedule root so we intercept
        // whichever internal Syncfusion element actually scrolls, regardless of version-specific class names.
        if (contentScrollHandlerRef.current && contentWrapRef.current) {
            contentWrapRef.current.removeEventListener('scroll', contentScrollHandlerRef.current, true)
        }
        contentWrapRef.current = scheduleEl
        contentScrollHandlerRef.current = (e) => {
            if (!overlayInnerRef.current) return
            // Filter to the main content area: ignore small dropdown/popup scroll events
            if (e.target.scrollHeight > e.target.clientHeight + 100) {
                overlayInnerRef.current.style.transform = `translateY(-${e.target.scrollTop}px)`
            }
        }
        scheduleEl.addEventListener('scroll', contentScrollHandlerRef.current, true)
    }

    useEffect(() => {
        return () => {
            if (contentWrapRef.current && contentScrollHandlerRef.current) {
                contentWrapRef.current.removeEventListener('scroll', contentScrollHandlerRef.current, true)
            }
        }
    }, [])

    const handleTopScroll = (e) => {
        if (syncingScroll.current) return
        syncingScroll.current = true
        if (mainScrollRef.current) mainScrollRef.current.scrollLeft = e.target.scrollLeft
        syncingScroll.current = false
    }

    const handleMainScroll = (e) => {
        if (syncingScroll.current) return
        syncingScroll.current = true
        if (topScrollRef.current) topScrollRef.current.scrollLeft = e.target.scrollLeft
        // Show/hide/position the overlay via direct DOM — no React state, no re-renders
        if (overlayRef.current && settings.horizontalScroll) {
            const scrollLeft = e.target.scrollLeft
            const visible = scrollLeft > TIME_COLUMN_WIDTH
            overlayRef.current.style.display = visible ? 'block' : 'none'
            if (visible) {
                overlayRef.current.style.left = mainScrollRef.current.getBoundingClientRect().left + 'px'
            }
        }
        syncingScroll.current = false
    }

    useEffect(() => {
        const updateHeight = () => {
            if (!schedulerContainerRef.current) return
            const top = schedulerContainerRef.current.getBoundingClientRect().top
            const available = window.innerHeight - top - 16
            setSchedulerHeight(`${Math.max(available, 300)}px`)
        }
        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [ownerData, selectedDock, settings.horizontalScroll, settings.widescreenMode])


    const contextValue = { docks, setDocks };

    useEffect(() => {
        // Setting the selected dock to be the first dock by default only if no dock is yet selected
        if (unScheduledShipments) {
            // console.log('unScheduledShipments', unScheduledShipments)
            let pickup = 0
            let delivery = 0
            if (unScheduledShipments.pickup.length) {
                pickup = unScheduledShipments.pickup.length
            }
            if (unScheduledShipments.delivery.length) {
                delivery = unScheduledShipments.pickup.length
            }
            let total = pickup + delivery
            setManagerModeBtnText('Show Unscheduled Shipments (' + total + ')')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unScheduledShipments])



    useEffect(() => {
        // Setting the selected dock to be the first dock by default only if no dock is yet selected
        if (docks && !selectedDock) {
            setSelectedDock(docks[0])
        }

        // Setting owner data (this assigns colors and "owners" to various docks so that syncusion can display them together using ResourcesDirective) https://ej2.syncfusion.com/react/documentation/schedule/resources/

        if (docks && !selectedDock) {
            let docksNo9999 = docks.filter(dock => {
                if (dock.id !== 99999999999999) {
                    return dock
                }
            })
            let generatedOwnerData = docksNo9999.map((dock, index) => {
                let hue = ((index + 1) * 137.508) % 360
                let color = `hsl(${hue.toFixed(1)}, 55%, 88%)`
                let ownerD = {}
                ownerD.OwnerText = dock.name
                ownerD.id = dock.id
                ownerD.OwnerColor = color
                ownerD.inbound = dock.inbound
                ownerD.outbound = dock.outbound
                return ownerD
            })
            setOwnerData(generatedOwnerData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docks])

    // If the currently selected group is deleted from settings, fall back to first dock
    useEffect(() => {
        if (selectedDock?.isGroup && docks) {
            const groupId = selectedDock.id.replace('group_', '')
            const groupStillExists = settings.dockGroups.some(g => g.id === groupId)
            if (!groupStillExists) {
                setSelectedDock(docks[0])
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.dockGroups])

    // console.log('managerMode', managerMode)

    // Calculate minimum content width for horizontal scroll mode.
    // Syncfusion sets column widths via JS (inline styles), so we can't use CSS min-width on cells.
    // Instead, force the container to be wide enough — Syncfusion fills its container and divides
    // the total width evenly across resource columns.
    //
    // The tab row always shows ALL docks, so we base the min-width on total dock count.
    // This keeps tabs readable regardless of which dock/group is selected.
    const numRealDocks = ownerData ? ownerData.length : 0
    const contentMinWidth = (settings.horizontalScroll && numRealDocks > 1)
        ? numRealDocks * settings.minDockColumnWidth
        : undefined

    // Suggest settings when there are many docks and no layout config has been applied yet,
    // and the user hasn't opened the settings modal before on this location
    const suggestSettings = numRealDocks >= 12
        && !settings.horizontalScroll
        && settings.dockGroups.length === 0
        && !settings.settingsSeen

    if (pageMode === 'running' && docks && selectedDock && ownerData) {
        if (managerMode === 'calendar') {
            return (
                <>

                    <SelectedDateProvider initialDate={urlDate ? (() => { const [y, m, d] = urlDate.split('-').map(Number); return new Date(y, m - 1, d) })() : null}>

                            <LoadingOverlay
                                active={updatingOverlayStatus}
                                spinner
                                text='Updating...'
                                styles={{
                                    overlay: (base) => ({
                                        ...base,
                                        position: 'fixed',
                                        zIndex: 9999
                                    })
                                }}
                            >

                                <Navbar
                                    usercode={usercode}
                                    status={status}
                                    docks={docks}
                                    selectedDock={selectedDock}
                                    userRole={userRole}
                                    lastUpdateDate={lastUpdateDate}
                                    location={location}
                                    managerMode={managerMode}
                                    setManagerMode={setManagerMode}
                                    widescreenMode={settings.widescreenMode}
                                    onOpenSettings={() => {
                                        if (!settings.settingsSeen) updateSettings({ ...settings, settingsSeen: true })
                                        setShowSettingsModal(true)
                                    }}
                                    suggestSettings={suggestSettings}
                                />
                                <AuthUI />
                                <Container fluid={settings.widescreenMode}>
                                    <Col>

                                        <Row>
                                            <hr className='mb-3 mt-2' />
                                        </Row>
                                    </Col>
                                    <Col>
                                        <Row>
                                            {settings.horizontalScroll && contentMinWidth && (
                                                <div
                                                    ref={topScrollRef}
                                                    onScroll={handleTopScroll}
                                                    style={{ overflowX: 'scroll', overflowY: 'hidden', height: '12px', marginBottom: '2px' }}
                                                >
                                                    <div style={{ minWidth: contentMinWidth, height: '1px' }} />
                                                </div>
                                            )}
                                            <div
                                                ref={mainScrollRef}
                                                onScroll={settings.horizontalScroll ? handleMainScroll : undefined}
                                                style={settings.horizontalScroll ? { overflowX: 'auto' } : {}}
                                            >
                                                <div style={contentMinWidth ? { minWidth: contentMinWidth } : {}}>
                                                <DockSelector
                                                    docks={docks}
                                                    selectedDock={selectedDock}
                                                    setSelectedDock={setSelectedDock}
                                                    dockGroups={settings.dockGroups}
                                                />

                                                <div ref={schedulerContainerRef}>

                                                {/* Individual dock render path */}
                                                {!selectedDock?.isGroup && docks.map((dock, index) => {
                                                    if (JSON.stringify(dock.name) === JSON.stringify(selectedDock.name)) {
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <DocksManagerSchedules selectedDock={dock} docks={docks} usercode={usercode} setUpdatingOverlayStatus={setUpdatingOverlayStatus} getManagerView={getManagerView} setStatus={setStatus} refreshData={refreshData} ownerData={ownerData} userRole={userRole} locationSettings={locationSettings} showDockTypeOnHeader={settings.showDockTypeOnHeader} height={schedulerHeight} onScheduleReady={handleScheduleReady} applyOptimisticUpdate={applyOptimisticUpdate} />
                                                            </React.Fragment>
                                                        )
                                                    }
                                                    return null
                                                })}

                                                {/* Group dock render path */}
                                                {selectedDock?.isGroup && (() => {
                                                    const groupOwnerData = ownerData.filter(o => selectedDock.dockIds.includes(o.id))
                                                    if (groupOwnerData.length === 0) {
                                                        return (
                                                            <div className="alert alert-warning mt-3">
                                                                This group has no valid docks. Open Settings to edit your groups.
                                                            </div>
                                                        )
                                                    }
                                                    const groupEvents = docks.reduce((acc, d) => {
                                                        if (selectedDock.dockIds.includes(d.id) && d.events) {
                                                            return acc.concat(d.events)
                                                        }
                                                        return acc
                                                    }, [])
                                                    const groupDockForScheduler = {
                                                        ...selectedDock,
                                                        id: 99999999999999,
                                                        events: groupEvents
                                                    }
                                                    return (
                                                        <DocksManagerSchedules
                                                            selectedDock={groupDockForScheduler}
                                                            docks={docks}
                                                            usercode={usercode}
                                                            setUpdatingOverlayStatus={setUpdatingOverlayStatus}
                                                            getManagerView={getManagerView}
                                                            setStatus={setStatus}
                                                            refreshData={refreshData}
                                                            ownerData={groupOwnerData}
                                                            userRole={userRole}
                                                            locationSettings={locationSettings}
                                                            showDockTypeOnHeader={settings.showDockTypeOnHeader}
                                                            height={schedulerHeight}
                                                            onScheduleReady={handleScheduleReady}
                                                            applyOptimisticUpdate={applyOptimisticUpdate}
                                                        />
                                                    )
                                                })()}

                                                </div>{/* end schedulerContainerRef */}
                                                </div>{/* end inner min-width div */}
                                            </div>{/* end main scroll container */}

                                        </Row>

                                    </Col>
                                </Container>
                                <SettingsModal
                                    show={showSettingsModal}
                                    onHide={() => setShowSettingsModal(false)}
                                    settings={settings}
                                    updateSettings={updateSettings}
                                    docks={docks}
                                    locationId={location}
                                />
                                <PoweredByFooter />
                                {/* Sticky time overlay — position:fixed, driven by DOM refs only, never causes re-renders */}
                                <div
                                    ref={overlayRef}
                                    className="time-overlay-container"
                                    style={{ display: 'none', position: 'fixed', left: 0, zIndex: 1000, overflow: 'hidden', pointerEvents: 'none' }}
                                >
                                    <div ref={overlayInnerRef} />
                                </div>
                            </LoadingOverlay>
                    </SelectedDateProvider >

                </>)
        }

        if (managerMode === 'unScheduledShipments') {
            return (
                <SelectedDateProvider>
                    <LoadingOverlay
                        active={updatingOverlayStatus}
                        spinner
                        text='Updating...'
                    >
                        <Navbar status={status} docks={docks} selectedDock={selectedDock} />

                        <Container>

                            <Row>
                                <div className="d-flex flex-row">
                                    <ModeDropDown managerMode={managerMode} setManagerMode={setManagerMode} />
                                </div>
                            </Row>

                            <UnScheduledShipments unScheduledShipments={unScheduledShipments} />
                        </Container>
                        <PoweredByFooter />
                    </LoadingOverlay>
                </SelectedDateProvider>
            )
        }

        if (managerMode === 'unapprovedAppointments') {
            return (
                <SelectedDateProvider>
                    <LoadingOverlay
                        active={updatingOverlayStatus}
                        spinner
                        text='Updating...'
                    >
                        <Navbar status={status} docks={docks} selectedDock={selectedDock} />

                        <Container>
                            <Row>
                                <div className="d-flex flex-row">
                                    <ModeDropDown managerMode={managerMode} setManagerMode={setManagerMode} />
                                </div>
                            </Row>

                            <UnapprovedAppts />
                        </Container>
                        <PoweredByFooter />
                    </LoadingOverlay>
                </SelectedDateProvider>
            )
        } else {
            return (<div>WAT</div>)
        }
    } else if (pageMode === 'updating') {
        return (
            <>
                <Navbar />
                <Container>
                    <Col>
                        <Row>
                            <Updating />
                        </Row>
                    </Col>
                </Container>
            </>
        )

    } else if (pageMode === 'unauthorized') {
        return (
            <>
                <Navbar />
                <Container>
                    <Col>
                        <Row>
                            <Unauthorized />
                        </Row>
                    </Col>
                </Container>
            </>
        )
    } else {
        return (
            <>
                <Navbar />
                <Container>
                    <Col>
                        <Row>
                            <ManagerLoading />
                        </Row>
                    </Col>
                </Container>
            </>)
    }
}

export default Manager
